import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Clock, DollarSign, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CAT_COLORS = { development: "bg-blue-500/10 text-blue-400", design: "bg-purple-500/10 text-purple-400", meeting: "bg-yellow-500/10 text-yellow-400", project_management: "bg-[#00F0FF]/10 text-[#00F0FF]", support: "bg-green-500/10 text-green-400", testing: "bg-orange-500/10 text-orange-400", consulting: "bg-pink-500/10 text-pink-400", other: "bg-white/5 text-neutral-400" };

export default function TimeReportPage() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/time-report/${token}`)
      .then(r => setReport(r.data))
      .catch(() => setError("Report not found"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#00F0FF] animate-spin" /></div>;
  if (error) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="text-center"><AlertCircle className="w-12 h-12 text-neutral-600 mx-auto mb-4" /><p className="text-neutral-500">{error}</p></div></div>;

  const s = report.summary;
  const grandTotal = s.total_cost + s.total_expenses;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="time-report-page">
      <nav className="border-b border-white/5 bg-[#050505]/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <span className="font-outfit font-medium text-white text-lg tracking-tight">wedowedo</span>
          <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-600">TIME REPORT</span>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="font-outfit font-bold text-3xl text-white mb-1">{report.project_title}</h1>
          <p className="text-sm text-neutral-500">{report.customer_name}</p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <div className="bg-[#0a0a0a] border border-white/5 rounded-sm p-4">
            <span className="font-mono text-[10px] text-neutral-600">HOURS</span>
            <div className="font-outfit font-bold text-xl text-white mt-1">{s.total_hours}h</div>
          </div>
          <div className="bg-[#0a0a0a] border border-white/5 rounded-sm p-4">
            <span className="font-mono text-[10px] text-neutral-600">TIME COST</span>
            <div className="font-outfit font-bold text-xl text-[#00F0FF] mt-1">{s.total_cost.toLocaleString()} {s.currency}</div>
          </div>
          <div className="bg-[#0a0a0a] border border-white/5 rounded-sm p-4">
            <span className="font-mono text-[10px] text-neutral-600">EXPENSES</span>
            <div className="font-outfit font-bold text-xl text-white mt-1">{s.total_expenses.toLocaleString()} {s.currency}</div>
          </div>
          <div className="bg-[#0a0a0a] border border-[#00F0FF]/20 rounded-sm p-4">
            <span className="font-mono text-[10px] text-[#00F0FF]">TOTAL</span>
            <div className="font-outfit font-bold text-xl text-[#00F0FF] mt-1">{grandTotal.toLocaleString()} {s.currency}</div>
          </div>
        </div>

        {/* Entries table */}
        <div className="bg-[#0a0a0a] border border-white/5 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-neutral-600">DATE</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-neutral-600">CATEGORY</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-neutral-600">DESCRIPTION</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-neutral-600">HOURS</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-neutral-600">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {report.entries.map((e, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="px-4 py-3 text-xs text-neutral-500 font-mono">{e.date}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-mono ${CAT_COLORS[e.category] || CAT_COLORS.other}`}>{e.category?.replace("_", " ")}</span></td>
                  <td className="px-4 py-3 text-sm text-neutral-300">{e.description}</td>
                  <td className="px-4 py-3 text-sm text-neutral-400 font-mono text-right">
                    {e.entry_type === "time" ? `${e.hours}h` : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-white font-mono text-right">
                    {e.entry_type === "time" ? (e.hours * e.hourly_rate).toLocaleString() : e.expense_amount?.toLocaleString()} {e.currency}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-white/[0.02]">
                <td colSpan={3} className="px-4 py-3 text-sm text-neutral-500 font-medium">Total</td>
                <td className="px-4 py-3 text-sm text-white font-mono text-right font-medium">{s.total_hours}h</td>
                <td className="px-4 py-3 text-sm text-[#00F0FF] font-mono text-right font-bold">{grandTotal.toLocaleString()} {s.currency}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="text-center mt-10 pt-8 border-t border-white/5">
          <p className="text-xs text-neutral-700 font-mono">{report.provider.name} &middot; Reg: {report.provider.reg} &middot; VAT: {report.provider.vat}</p>
        </div>
      </div>
    </div>
  );
}
